import {
  ReferenceNameTokens,
  applyReferenceNameTemplate,
  formatReferenceLabelFromComponents,
  resolveReferenceDisplayName,
  type ReferenceLabelEntry,
} from './apply-reference-name-template';

const TEMPLATE_TOKEN_RE = /\{\{type\}\}|\{\{number\}\}/;

export type LostReferenceLabelInput = {
  customName?: string | null;
  refId?: string | null;
  displayName?: string;
  detectedPhrase?: string | null;
};

/** Strip template tokens; join remaining literal segments for lost-ref fallback. */
export const literalSegmentsFromCustomName = (
  customName: string | null | undefined,
): string => {
  if (customName == null) return '';
  return customName
    .replaceAll(ReferenceNameTokens.type, ' ')
    .replaceAll(ReferenceNameTokens.number, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const customNameHasTemplateTokens = (
  customName: string | null | undefined,
): boolean => {
  const trimmed = customName?.trim() ?? '';
  return (
    trimmed.includes(ReferenceNameTokens.type) ||
    trimmed.includes(ReferenceNameTokens.number)
  );
};

const displayNameHasArtifacts = (
  displayName: string,
  refId: string | null,
): boolean =>
  TEMPLATE_TOKEN_RE.test(displayName) ||
  (refId != null && refId.length > 0 && displayName.includes(refId));

/** True when a label looks like an unresolved ref id or template artifact. */
export const isReferenceDisplayNameArtifact = displayNameHasArtifacts;

/** Positional target label (e.g. "Rule 1.1.1") from a label map entry. */
const targetPositionalLabel = (
  labelEntry: ReferenceLabelEntry | undefined,
): string => {
  if (!labelEntry) return '';
  const fromDisplay = labelEntry.displayName.trim();
  if (fromDisplay.length > 0 && !TEMPLATE_TOKEN_RE.test(fromDisplay)) {
    return fromDisplay;
  }
  return formatReferenceLabelFromComponents(labelEntry);
};

const joinLiteralWithTargetLabel = (
  literal: string,
  targetLabel: string,
): string => {
  if (!literal) return targetLabel;
  if (!targetLabel) return literal;
  if (literal.includes(targetLabel)) return literal;
  if (targetLabel.includes(literal)) return targetLabel;
  return [literal, targetLabel].filter(Boolean).join(' ').trim();
};

/**
 * User-facing label for a reference whose target block no longer exists.
 * Preserves literal prefix text and appends the target type + number when
 * the template tokens can no longer be resolved from a live label map entry.
 */
export const resolveDeleteReplacementText = (
  row: LostReferenceLabelInput,
  labelEntry: ReferenceLabelEntry | undefined,
): string => {
  const apiDisplayName = row.displayName?.trim() ?? '';
  const customName = row.customName ?? null;
  const refId = row.refId ?? null;
  const customNameTrimmed = customName?.trim() ?? '';
  const customHasTokens = customNameHasTemplateTokens(customName);
  const targetLabel = targetPositionalLabel(labelEntry);

  if (customHasTokens && labelEntry) {
    const expanded = applyReferenceNameTemplate(
      customNameTrimmed,
      {
        typeLabel: labelEntry.typeLabel,
        number: labelEntry.number,
        titleSuffix: labelEntry.titleSuffix,
      },
      refId ?? '',
    );
    if (!displayNameHasArtifacts(expanded, refId)) {
      return expanded;
    }
  }

  if (customNameTrimmed.length > 0 && !customHasTokens) {
    return customNameTrimmed;
  }

  if (
    apiDisplayName.length > 0 &&
    !displayNameHasArtifacts(apiDisplayName, refId)
  ) {
    const literalPrefix = literalSegmentsFromCustomName(customName);
    if (!customHasTokens || literalPrefix.length === 0) {
      return apiDisplayName;
    }
    if (apiDisplayName.includes(literalPrefix)) {
      return apiDisplayName;
    }
  }

  const resolved = resolveReferenceDisplayName(customName, refId, labelEntry);
  if (!displayNameHasArtifacts(resolved.displayName, refId)) {
    return resolved.displayName;
  }

  const literal = literalSegmentsFromCustomName(customName);

  if (
    literal.length > 0 &&
    apiDisplayName.length > 0 &&
    !displayNameHasArtifacts(apiDisplayName, refId) &&
    !apiDisplayName.includes(literal)
  ) {
    const joined = joinLiteralWithTargetLabel(literal, apiDisplayName);
    if (!displayNameHasArtifacts(joined, refId)) {
      return joined;
    }
  }

  if (literal || targetLabel) {
    const joined = joinLiteralWithTargetLabel(literal, targetLabel);
    if (!displayNameHasArtifacts(joined, refId)) {
      return joined;
    }
  }

  if (targetLabel.length > 0) {
    return targetLabel;
  }

  if (literal.length > 0) {
    return literal;
  }

  return apiDisplayName;
};

export type ReferenceRecoveryPhraseInput = {
  customName?: string | null;
  refId?: string | null;
};

/**
 * Fully expanded user-facing label from a live label map entry.
 * Used when snapshotting recovery text before a target block is deleted.
 */
export const computeReferenceRecoveryPhrase = (
  row: ReferenceRecoveryPhraseInput,
  labelEntry: ReferenceLabelEntry | undefined,
): string => {
  const refId = row.refId ?? null;
  if (!labelEntry) return '';

  const customName = row.customName ?? null;
  const customNameTrimmed = customName?.trim() ?? '';
  const customHasTokens = customNameHasTemplateTokens(customName);

  if (customHasTokens) {
    const expanded = applyReferenceNameTemplate(
      customNameTrimmed,
      {
        typeLabel: labelEntry.typeLabel,
        number: labelEntry.number,
        titleSuffix: labelEntry.titleSuffix,
      },
      refId ?? '',
    );
    if (!displayNameHasArtifacts(expanded, refId)) {
      return expanded;
    }
  }

  if (customNameTrimmed.length > 0 && !customHasTokens) {
    return customNameTrimmed;
  }

  const resolved = resolveReferenceDisplayName(customName, refId, labelEntry);
  if (!displayNameHasArtifacts(resolved.displayName, refId)) {
    return resolved.displayName;
  }

  const targetLabel = targetPositionalLabel(labelEntry);
  if (targetLabel.length > 0) {
    return targetLabel;
  }

  return '';
};

const sanitizeLostReferenceLabel = (
  label: string,
  row: LostReferenceLabelInput,
): string => {
  const refId = row.refId ?? null;
  const trimmed = label.trim();
  if (trimmed.length === 0) return trimmed;
  if (!displayNameHasArtifacts(trimmed, refId)) {
    return trimmed;
  }

  const phrase = row.detectedPhrase?.trim() ?? '';
  if (phrase.length > 0 && !displayNameHasArtifacts(phrase, refId)) {
    return phrase;
  }

  const customName = row.customName ?? null;
  const customNameTrimmed = customName?.trim() ?? '';
  if (
    customNameTrimmed.length > 0 &&
    !customNameHasTemplateTokens(customName)
  ) {
    return customNameTrimmed;
  }

  const literal = literalSegmentsFromCustomName(customName);
  if (literal.length > 0) {
    return literal;
  }

  return '';
};

/** Display label for an unresolved internal reference row. */
export const resolveLostReferenceDisplayName = (
  row: LostReferenceLabelInput,
  labelEntry: ReferenceLabelEntry | undefined,
  unresolvedDisplayName: string,
): string => {
  const phrase = row.detectedPhrase?.trim() ?? '';
  const refId = row.refId ?? null;
  if (phrase.length > 0 && !displayNameHasArtifacts(phrase, refId)) {
    return phrase;
  }

  const resolved = resolveDeleteReplacementText(
    {
      customName: row.customName,
      refId,
      displayName: unresolvedDisplayName,
      detectedPhrase: row.detectedPhrase,
    },
    labelEntry,
  );

  return sanitizeLostReferenceLabel(resolved, row);
};

export const isLostInternalReference = (row: {
  kind?: string;
  status?: string;
  resolved?: boolean;
}): boolean =>
  (row.kind ?? 'internal') === 'internal' &&
  row.resolved === false &&
  row.status !== 'ignored';
